import type { FC, ReactElement, RefObject } from 'react';
import { Picture } from '@ui';
import { observer } from 'mobx-react';
import icons from '@icons';
import useWindowSize from '@customHooks/useWindowSize';
import StoreInfoCard from '../../StoreInfo/StoreInfoCard';
import DelayedMapRender from '../components/DelayedMapRender';
import DeliveryTypeToggle from '../components/DeliveryTypeToggle';
import MapListToggle from '../components/MapListToggle';
import ModalHeader from '../components/ModalHeader';
import PickupSearchInput from '../components/PickupSearchInput';
import StoreListView from '../components/StoreListView';
import type { PlacemarkI } from '@components/MapRender';
import { MAP_ZOOM } from '../constants';
import { getStoreAvailableQuantity } from '../utils';
import type { AvailableQuantityItem, StoreListEntry } from '../types';
import PriceChangeWarning from '@components/PriceChangeWarning';

const {
  bonus: { red: bonusRubleRed }
} = icons.Outline;

interface PickupStageProps {
  isHideDelivery?: boolean;
  isShowAvailableQuantity?: boolean;
  availableQuantity?: AvailableQuantityItem[];
  cashbackForPickup: number | string | null | undefined;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filteredShops: StoreListEntry[];
  selectedShop: StoreListEntry;
  storeId: number;
  setStoreId: (id: number) => void;
  elRefs?: Array<RefObject<HTMLDivElement>>;
  placemarks: PlacemarkI[];
  mapCoordsShops: number[];
  isShowMap: boolean;
  setIsShowMap: (val: boolean) => void;
  onChangeType: (id: number) => () => void;
  closeModal: () => void;
  onSubmit: () => void;
  address?: string;
}

const PickupStage: FC<PickupStageProps> = observer(
  ({
    isHideDelivery,
    isShowAvailableQuantity,
    availableQuantity,
    cashbackForPickup,
    searchValue,
    onSearchChange,
    filteredShops,
    selectedShop,
    storeId,
    setStoreId,
    elRefs,
    placemarks,
    mapCoordsShops,
    isShowMap,
    setIsShowMap,
    onChangeType,
    closeModal,
    onSubmit,
    address
  }) => {
    const { isMobile } = useWindowSize();
    const showCashbackBadge = !!Number(cashbackForPickup);

    const renderMap = (mapKey: string, isMobileMap = false): ReactElement => (
      <DelayedMapRender
        key={mapKey}
        center={mapCoordsShops}
        zoom={MAP_ZOOM}
        placemark={placemarks}
        isHiddenControl
        modules={['templateLayoutFactory', 'layout.ImageWithContent']}
        isShowClusterer={false}
        width={isMobileMap ? '100vw' : '100%'}
        height='100%'
      />
    );

    const storeList = (availableLabelPrefix?: string): ReactElement => (
      <StoreListView
        shops={filteredShops}
        selectedId={storeId}
        onSelect={setStoreId}
        elRefs={elRefs}
        isShowAvailableQuantity={isShowAvailableQuantity}
        availableQuantity={availableQuantity}
        availableLabelPrefix={availableLabelPrefix}
      />
    );

    return (
      <>
        <div className='left'>
          <div className='desktop'>
            {!isHideDelivery && (
              <>
                <DeliveryTypeToggle
                  isPickupCart={true}
                  onChangeType={onChangeType}
                />
                <PriceChangeWarning className='mb-3 rounded-lg' />
              </>
            )}

            <div className='regionTitle'>
              <span className='title'>
                {isHideDelivery ? 'Выберите пункт выдачи' : 'Укажите ваш адрес'}
              </span>
            </div>

            <PickupSearchInput
              className='modal-pickup-notLogged__search'
              value={searchValue}
              onChange={onSearchChange}
            />

            <div className='description'>
              Введите адрес, выберете из списка или на карте. Покажем наличие
              товаров и актуальные условия доставки
            </div>

            {showCashbackBadge && (
              <span className='shop-bonus-red'>
                <Picture icon={bonusRubleRed} height='20' />
                {`Вернем ${cashbackForPickup}% бонусами при самовывозе`}
              </span>
            )}

            <StoreInfoCard
              isShowAdditionalInfo={true}
              loading={false}
              store={selectedShop}
              storeAvailableQuantity={getStoreAvailableQuantity(
                storeId,
                availableQuantity
              )}
              isShowAvailableQuantity={isShowAvailableQuantity}
            />
          </div>

          <div className='header-info'>
            <ModalHeader title='Выберите пункт выдачи' onClose={closeModal} />
            <MapListToggle isShowMap={isShowMap} setIsShowMap={setIsShowMap} />
          </div>

          <div className='body-info'>
            {isMobile &&
              (isShowMap ? (
                renderMap('guest-pickup-map-mobile', true)
              ) : (
                <>
                  <PickupSearchInput
                    className='modal-pickup-notLogged__mobile-search'
                    value={searchValue}
                    onChange={onSearchChange}
                  />

                  <span className='shop-bonus-red'>
                    <Picture icon={bonusRubleRed} height='20' />
                    {Number(cashbackForPickup) > 0
                      ? `Вернем ${cashbackForPickup}% бонусами при самовывозе`
                      : ''}
                  </span>

                  {storeList()}
                </>
              ))}
          </div>

          <div className='footer-info'>
            <div className='mobile-info'>
              {!isHideDelivery && (
                <>
                  <DeliveryTypeToggle
                    isPickupCart={true}
                    onChangeType={onChangeType}
                  />
                  <PriceChangeWarning className='mb-3 rounded-lg' />
                </>
              )}

              {isShowMap ? (
                <StoreInfoCard
                  isShowAdditionalInfo={true}
                  loading={false}
                  store={selectedShop}
                  isMobile
                />
              ) : address ? (
                <div className='homeTitle'>{address}</div>
              ) : null}
            </div>
            <div onClick={onSubmit} className='confirm-modal'>
              Подтвердить
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className='right'>
            <div className='smartLoggedMap'>
              <div className='changerMap'>
                <MapListToggle
                  isShowMap={isShowMap}
                  setIsShowMap={setIsShowMap}
                />
              </div>
              {isShowMap ? (
                renderMap('guest-pickup-map-desktop')
              ) : (
                <>
                  <div className='space' />
                  {storeList('В наличии: ')}
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
);

export default PickupStage;
